import {select, Selection, scaleBand, scaleLinear, axisBottom, axisLeft, max, ScaleLinear, AxisScale, rgb} from "d3";
import { BaseChartComponent } from "../base-chart.component";
import { ChartTooltip } from "../chart-tooltip.component";
import {BarChartLegend} from "./components/bar-chart-legend.component";
import {BarChartData} from "./models/bar-chart-data.model";
import {BarChartItem} from "./models/bar-chart-item.model";
import {BarChartEntry} from "./models/bar-chart-entry.model";
import {AxisConfig} from "./models/axis-config.model";

export class BarChartComponent extends BaseChartComponent {
    private tooltip: ChartTooltip;
    private legend: BarChartLegend;
    private readonly MARGIN = { horizontal: 50, vertical: 50 };
    private width: number;
    private height: number;
    protected chartType = "bar";
    protected _data: BarChartData = null;

    protected initComponent(context: JQuery<HTMLElement>, input: BarChartData): void {
        this._data = input || this._data;
        super.init(context);
    }

    public static init(context: JQuery<HTMLElement>, input: BarChartData): BarChartComponent {
        const component = new BarChartComponent();
        component.initComponent(context, input);
        return component;
    }

    public createChart(): void {
        super.createChart();

        const chartClientBox = this.chart.node().getBoundingClientRect();
        this.width = chartClientBox.width;
        this.height = chartClientBox.height;

        this.chart.attr("viewBox", `-${this.MARGIN.vertical} -${this.MARGIN.horizontal} ${this.width + 2 * this.MARGIN.vertical} ${this.height + 2 * this.MARGIN.horizontal}`);

        this.legend = new BarChartLegend(this._currentContext,
            Object.keys(this._data.bars).map(key => this._data.bars[key]),
            {
                handlers: {
                    onClick: (d: BarChartItem, el: HTMLElement): void => {
                        d.visible = !d.visible;
                        this.legend.disable(el, !d.visible);
                        this.renderChart();
                    }
                }
            }
        );

        this.tooltip = new ChartTooltip(this._currentContext);
    }

    public renderChart(): void {
        const visibleBars = Object.keys(this._data.bars)
            .map(key => $.extend({alias: key}, this._data.bars[key]))
            .filter(bar => bar.visible)
            .map(bar => bar.alias);

        const yAxisData = (this._data.axis || {}).y || {};
        const yDomain = yAxisData.domain || {};
        const yDomainRange = yDomain.range
            ? yDomain.range
            : [0, max(this._data.entries, (d: BarChartEntry) => {
                return max(visibleBars, (key: string) => {
                    const value = d[key as keyof BarChartEntry];
                    return typeof value === "number" ? value : 0;
                }) || 0;
            })];

        const yScale = scaleLinear()
            .domain(yDomainRange)
            .rangeRound([this.height, 0])
            .nice();

        this.renderAxisY(yScale, yAxisData);

        const xAxisData = (this._data.axis || {}).x || {};
        const xScale: AxisScale<string> = scaleBand()
            .domain(this._data.entries.map(d => d.group))
            .rangeRound([0, this.width])
            .paddingInner(0.1);

        this.renderAxisX(xScale, xAxisData);

        this.renderBars(xScale, yScale, visibleBars);
    }

    private renderAxisY(yScale: ScaleLinear<number, number>, yAxisData: AxisConfig): void {
        const yAxis = this.chart
            .selectAll(".pcg-plot__axis.pcg-plot__axis--y")
            .data([null])
            .enter().append("g")
            .attr("class", "pcg-plot__axis pcg-plot__axis--y")
            .merge(this.chart.selectAll(".pcg-plot__axis.pcg-plot__axis--y"));

        yAxis
            .call(axisLeft(yScale)
                .tickFormat(yAxisData.tickFormat ? yAxisData.tickFormat : null)
                .ticks(yAxisData.ticks ? yAxisData.ticks : null)
            );

        this.enrichAxisByCss(yAxis);

        const borders = yAxis
            .selectAll(".pcg-plot__axis-tick .pcg-plot__axis-border")
            .data([null])
            .enter().append("line")
            .attr("class", "pcg-plot__axis-border")
            .merge(yAxis.selectAll(".pcg-plot__axis-tick .pcg-plot__axis-border"));

        borders
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", this.width)
            .attr("y2", 0);

        const yDomain = yAxisData.domain || {};

        if (yDomain.highlighted) {
            select(this._currentContext[0])
                .select(".pcg-plot__axis.pcg-plot__axis--y")
                .selectAll(".pcg-plot__axis-border.pcg-plot__axis-border--highlighted")
                .data(yDomain.highlighted)
                .enter().append("line")
                .attr("class", "pcg-plot__axis-border pcg-plot__axis-border--highlighted")
                .attr("x1", 0)
                .attr("y1", 0)
                .attr("x2", this.width)
                .attr("y2", 0)
                .attr("transform", (d: number) => `translate(0, ${yScale(d)})`);
        }

        if (yAxisData.label) {
            const label = yAxis
                .selectAll(".pcg-plot__axis-label")
                .data([null])
                .enter().append("text")
                .attr("class", "pcg-plot__axis-label")
                .merge(yAxis.selectAll(".pcg-plot__axis-label"));
            label
                .attr("transform", "rotate(-90)")
                .attr("x", 0 - this.height / 2)
                .attr("y", 0 - 1.2 * this.MARGIN.horizontal)
                .attr("text-anchor", "middle")
                .text(yAxisData.label);
        }
    }

    private renderAxisX(xScale: AxisScale<string>, xAxisData: AxisConfig): void {
        const xAxis = this.chart
            .selectAll(".pcg-plot__axis.pcg-plot__axis--x")
            .data([null])
            .enter().append("g")
            .attr("class", "pcg-plot__axis pcg-plot__axis--x")
            .merge(this.chart.selectAll(".pcg-plot__axis.pcg-plot__axis--x"));

        xAxis
            .attr("transform", `translate(0,${this.height})`)
            .call(axisBottom(xScale).tickFormat(xAxisData.tickFormat ?? null));

        this.enrichAxisByCss(xAxis);

        if (xAxisData.label) {
            const label = xAxis
                .selectAll(".pcg-plot__axis-label")
                .data([null])
                .enter().append("text")
                .attr("class", "pcg-plot__axis-label")
                .merge(xAxis.selectAll(".pcg-plot__axis-label"));

            label
                .attr("x", this.width / 2)
                .attr("y", this.MARGIN.horizontal)
                .attr("text-anchor", "middle")
                .text(xAxisData.label);
        }
    }

    private enrichAxisByCss(axis: Selection<SVGGElement, unknown, SVGSVGElement, unknown>) : void {
        axis
            .selectAll(".domain")
            .attr("class", "pcg-plot__axis-line domain");

        const tick = axis
            .selectAll(".tick")
            .attr("class", "pcg-plot__axis-tick tick");
        tick
            .selectAll("text")
            .attr("class", "pcg-plot__axis-text text");
        tick
            .selectAll("line:first-of-type")
            .attr("class", "pcg-plot__axis-dash");
    }

    private renderBars(xScale: AxisScale<string>, yScale: ScaleLinear<number, number>, visibleBars: Array<string>): void {
        const barsScale = scaleBand()
            .domain(visibleBars)
            .rangeRound([0, xScale.bandwidth()])
            .padding(0.05);

        const barGroupsWrap = this.chart
            .selectAll(".pcg-plot__groups")
            .data([null])
            .enter().append("g")
            .attr("class", "pcg-plot__groups")
            .merge(this.chart.selectAll(".pcg-plot__groups"));

        const barGroups = barGroupsWrap
            .selectAll(".pcg-plot__group")
            .data(this._data.entries)
            .enter().append("g")
            .attr("class", "pcg-plot__group")
            .attr("transform", (d) => `translate(${xScale(d.group)}, 0)`)
            .merge(barGroupsWrap.selectAll(".pcg-plot__group"));

        const bars = barGroups
            .selectAll<SVGRectElement, { group: string; alias: string; value: number }>(".pcg-plot__bar")
            .data(d => visibleBars
                .map(alias => ({
                    group: d.group,
                    alias: alias,
                    value: typeof d[alias] === "number" ? d[alias] as number : 0
                }))
                .filter(data => visibleBars.includes(data.alias))
            );

        bars.exit().remove();

        bars.enter().append("rect")
            .attr("class", "pcg-plot__bar")
            .merge(bars);
        bars
            .attr("x", d => barsScale(d.alias))
            .attr("y", this.height)
            .attr("width", barsScale.bandwidth())
            .attr("height", 0)
            .attr("fill", d => this._data.bars[d.alias].color)
            .attr("initial-fill", d => this._data.bars[d.alias].color);

        const tooltipData = this._data.tooltip || {};

        bars
            .on("mouseover", (event: MouseEvent, d: { group: string; alias: string; value: number }) => {
                const el = select(event.currentTarget as SVGPathElement);
                const hoverColor = rgb(el.style("fill")).brighter(0.1).toString();
                el.style("fill", hoverColor);

                const header = tooltipData.headerFormat ? tooltipData.headerFormat(d) : d.group;
                const text = tooltipData.textFormat ? tooltipData.textFormat(d) : `${this._data.bars[d.alias].label}: ${d.value}`;

                this.tooltip.show(header, text);
            })
            .on("mousemove", (event: MouseEvent) => {
                this.tooltip.move(event.clientX - 25, event.clientY + 35);})
            .on("mouseout", (event: MouseEvent) => {
                const el = select(event.currentTarget as SVGPathElement);
                el.style("fill", el.attr("initial-fill"));
                this.tooltip.hide();
            });

        bars
            .transition()
            .duration(1000)
            .attr("y", d => yScale(d.value))
            .attr("height", d => this.height - yScale(d.value));
    }
}
